/* istanbul ignore file */

// externals
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { Spinner } from '@interstate/components/Spinner';
import { SearchInput } from '@interstate/components/SearchInput';
import type { ToggleButtonOptions } from '@interstate/components/ToggleGroup';
import { ToggleGroup } from '@interstate/components/ToggleGroup';

// local components
import BuildVehicle from '../buildVehicle/BuildVehicle';
import ChangeVehicleSearchResult from './ChangeVehicleSearchResult';
import SearchBarError, { ErrorType } from './SearchBarError';
import VehicleAppliedFilterTags from './VehicleAppliedFilterTags';

// utils
import { getFilteredSearchResults } from '../../utils/filterSearchResults';

// types
import type { IVehicle } from '@makemydeal/dr-platform-types';
import { FilterSummaryGroups } from '../../types';

// styles
import { ChangeVehicleTabsContainer, SearchAndSelectVehicleContainer } from './ChangeVehicle.interstate.style';
import {
    SearchSpinnerContainer,
    ErrorMessageContainer,
    ErrorTextContainer,
    ErrorMessageIcon,
    StyledSearchBarContainer
} from './SearchAndSelectVehicle.style';

// store
import { changeVehicleCreators, changeVehicleSelectors } from '../../store';
import { useVehicleSearchFiltersContext } from './VehicleSearchFiltersContext';
import type { BuildVehicleInitValue } from '../buildVehicle/types';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import AiAssistedSearch from './AiAssistedSearch';

enum TABS {
    searchVehicle,
    buildVehicle
}

type TabIndex = TABS.searchVehicle | TABS.buildVehicle;

const tabsData = [
    {
        title: <div data-testid="search-vehicle-tab">Search Vehicle</div>,
        value: TABS.searchVehicle
    },
    {
        title: <div data-testid="build-vehicle-tab">Build Vehicle</div>,
        value: TABS.buildVehicle
    }
];

export interface SearchAndSelectVehicleContentProps {
    isMobileView: boolean;
    onTabChange?: (isSearchVehicleTabActive: boolean) => void;
}

const SEARCH_FIELD_MAX_LENGTH = 100;

const SearchAndSelectVehicleContent = ({ isMobileView, onTabChange }: SearchAndSelectVehicleContentProps) => {
    const dispatch = useDispatch();
    const searchBarPlaceholderV2 = 'Search VIN, Stock #, YMMT, Type';
    const searchFieldMaxLengthErrorMessage = 'The entered search request exceeds the limit of 100 characters.';

    const showFiltersPanel = useSelector(changeVehicleSelectors.getIsFiltersInitialized);
    const isSearchInProgress = useSelector(changeVehicleSelectors.getShowSearchLoader);
    const searchQuery = useSelector(changeVehicleSelectors.getSearchQuery);
    const vehicleVin = useSelector(changeVehicleSelectors.getVehicleVin);
    const vehicleStockNumber = useSelector(changeVehicleSelectors.getVehicleStockNumber);
    const searchResults = useSelector(changeVehicleSelectors.getSearchResults);

    // ai search values
    const isDealerAISearchEnabled = useSelector(dealerSelectors.isDealerAISearchEnabled);

    const isSearchError = useSelector(changeVehicleSelectors.getShowSearchError);
    const isFetching = useSelector(changeVehicleSelectors.getIsFetching);
    const { searchValue, appliedFilters, updateSearchValue } = useVehicleSearchFiltersContext();

    const { filteredSearchResults, isFilteredSearchError } = useMemo(
        () => getFilteredSearchResults(vehicleVin, vehicleStockNumber, searchResults),
        [vehicleVin, vehicleStockNumber, searchResults]
    );

    const [searchText, setSearchText] = useState(searchValue);
    const [isSearchDisabled, setSearchDisabled] = useState(false);
    const [activeTabIndex, setActiveTabIndex] = useState<TabIndex>(TABS.searchVehicle);
    const isSearchVehicleTabActive = activeTabIndex === TABS.searchVehicle;
    const [buildVehicleValue, setBuildVehicleValue] = useState<BuildVehicleInitValue>({
        vin: undefined,
        year: '',
        make: '',
        model: '',
        trim: ''
    });
    const hasSearchResults = (filteredSearchResults ?? []).length > 0;
    const showNoResultsFoundError = !hasSearchResults && !isFetching && !isSearchError && !isFilteredSearchError;

    const tabs: ToggleButtonOptions[] = tabsData.map(({ title, value }) => {
        const children = <div>{title}</div>;
        return {
            value,
            children,
            selected: activeTabIndex === value
        };
    });

    // Extract filter values for Build Vehicle tab
    const getFilterValuesForBuildVehicle = () => {
        // Get filter values, preserving original case for display
        const inventoryType = appliedFilters.find((filter) => filter.id === FilterSummaryGroups.INVENTORY_TYPES)?.label || '';
        const year = appliedFilters.find((filter) => filter.id === FilterSummaryGroups.YEARS)?.label || '';
        const make = appliedFilters.find((filter) => filter.id === FilterSummaryGroups.MAKES)?.label || '';
        const model = appliedFilters.find((filter) => filter.id === FilterSummaryGroups.MODELS)?.label || '';
        const trim = appliedFilters.find((filter) => filter.id === FilterSummaryGroups.TRIMS)?.label || '';

        return {
            vin: undefined,
            year,
            make,
            model,
            trim,
            inventoryType
        };
    };

    const containerRef = useRef<HTMLDivElement>(null);

    const redirectToBuildVehicleTab = (vehicle: IVehicle) => {
        // Row's own vin first: `searchQuery` is set only by the classic search creators, so it is empty
        // or stale in AI mode.
        const vin = vehicle.vin || searchQuery;
        const { make = '', model = '', trim = '', year = '', condition = '' } = vehicle;
        // Map condition to inventory type if available
        const inventoryType = condition === 'new' ? 'New' : condition === 'certified' ? 'CPO' : condition === 'used' ? 'Used' : '';
        setBuildVehicleValue({ vin, make, model, trim, year: `${year}`, inventoryType });
        setActiveTabIndex(TABS.buildVehicle);

        // Call onTabChange to hide filters, just like when switching tabs
        dispatch(changeVehicleCreators.changeVehicleTabChanged());
        if (onTabChange) {
            onTabChange(false);
        }

        containerRef.current?.scrollIntoView?.({ block: 'center' });
    };

    const hasMaxLengthError = searchText.length > SEARCH_FIELD_MAX_LENGTH;

    useEffect(() => {
        setSearchDisabled(hasMaxLengthError);
    }, [hasMaxLengthError]);

    return (
        <SearchAndSelectVehicleContainer className="search-select-vehicle-container">
            <ChangeVehicleTabsContainer ref={containerRef}>
                <ToggleGroup
                    onChange={(_e, value: TabIndex) => {
                        if (activeTabIndex !== value) {
                            setActiveTabIndex(value);

                            // When switching to Build Vehicle tab, use applied filters
                            if (value === TABS.buildVehicle) {
                                const filterValues = getFilterValuesForBuildVehicle();
                                setBuildVehicleValue(filterValues);
                            } else {
                                setBuildVehicleValue({
                                    vin: undefined,
                                    year: '',
                                    make: '',
                                    model: '',
                                    trim: '',
                                    inventoryType: ''
                                });
                            }

                            dispatch(changeVehicleCreators.changeVehicleTabChanged());
                            if (onTabChange) {
                                onTabChange(value === TABS.searchVehicle);
                            }
                        }
                    }}
                    toggleButtonOptions={tabs}
                    toggleWidth="50%"
                    size="medium"
                    id="change-vehicle-tabs"
                    data-testid="change-vehicle-tabs"
                />
            </ChangeVehicleTabsContainer>

            {isSearchVehicleTabActive ? (
                <>
                    {isDealerAISearchEnabled ? (
                        <AiAssistedSearch
                            {...{
                                isMobileView,
                                redirectToBuildVehicleTab
                            }}
                        />
                    ) : (
                        <div id="search-bar-container">
                            <StyledSearchBarContainer hasError={isSearchDisabled}>
                                <SearchInput
                                    id="search-bar-input"
                                    data-testid="change-vehicle-search-input"
                                    name="textInputAppend"
                                    placeholder={searchBarPlaceholderV2}
                                    inputValue={searchText}
                                    onInputChange={(event) => setSearchText(event.target.value || '')}
                                    onSearchClick={(event) => updateSearchValue(event.target.value)}
                                    onClearCallback={() => updateSearchValue('')}
                                    style="primary"
                                    disableSearch={isSearchDisabled}
                                />
                                {isSearchDisabled && (
                                    <ErrorMessageContainer data-testid="length-error-message">
                                        <ErrorMessageIcon />
                                        <ErrorTextContainer component="span">{searchFieldMaxLengthErrorMessage}</ErrorTextContainer>
                                    </ErrorMessageContainer>
                                )}
                            </StyledSearchBarContainer>
                            {showFiltersPanel && <VehicleAppliedFilterTags isMobileView={isMobileView} />}
                            {showNoResultsFoundError && <SearchBarError errorType={ErrorType.NO_RESULTS_FOUND} />}
                            {isFilteredSearchError && <SearchBarError errorType={ErrorType.CURRENT_VEHICLE} />}
                        </div>
                    )}
                    {filteredSearchResults && !isDealerAISearchEnabled && (
                        <ChangeVehicleSearchResult
                            searchResults={filteredSearchResults}
                            isSearchError={isSearchError}
                            redirectToBuildVehicleTab={redirectToBuildVehicleTab}
                            showSortOptions={true}
                        />
                    )}
                </>
            ) : activeTabIndex === TABS.buildVehicle ? (
                <div id="build-vehicle-content" data-testid="build-vehicle-content">
                    <BuildVehicle initValue={buildVehicleValue} />
                </div>
            ) : null}
            {isSearchInProgress && !isDealerAISearchEnabled && (
                <SearchSpinnerContainer>
                    <Spinner data-testid="search-vehicle-spinner" />
                </SearchSpinnerContainer>
            )}
        </SearchAndSelectVehicleContainer>
    );
};

export default SearchAndSelectVehicleContent;
